import { useNavigate } from '@tanstack/react-router';

import { CampaignsPage } from '@/features/campaigns/CampaignsPage';

import {
  campaignsRoute,
  campaignsSearchToInitialColumnFilters,
  campaignsSearchToWizardInit,
} from './routes';

interface CampaignsRouteProps {
  /** Teller variant (SCRUM-389): the ledger + drawer with campaign affordances stripped. */
  tellerMode?: boolean;
}

/**
 * Route adapter for /campaigns and /teller-dashboard (SCRUM-616). Reads the
 * deep-link search params (?inspect, ?wizard&targets&product&..., plan D4) into
 * CampaignsPage's initialInspectId / initialWizardInit props, and clears them
 * after consumption via replace-navigation. Teller mode passes tellerMode and
 * ignores the deep-link params (the teller route carries none).
 */
export function CampaignsRoute({ tellerMode = false }: CampaignsRouteProps) {
  const navigate = useNavigate();
  return tellerMode ? <CampaignsPage tellerMode /> : <CampaignsDeepLinkRoute navigate={navigate} />;
}

/**
 * The /campaigns variant that reads deep-link search params. Split into its own
 * component so `useSearch` (a hook) is only ever called on the route that
 * actually declares validateSearch — never conditionally (Rules of Hooks).
 */
function CampaignsDeepLinkRoute({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  const search = campaignsRoute.useSearch();
  const initialInspectId = search.inspect ?? null;
  const initialWizardInit = campaignsSearchToWizardInit(search);
  // SCRUM-847: KPI drill-through (?insight / ?reached) → seed the ledger column filters.
  const initialColumnFilters = campaignsSearchToInitialColumnFilters(search);

  const clearSearch = () => {
    void navigate({ to: '/campaigns', search: {}, replace: true });
  };

  return (
    <CampaignsPage
      initialInspectId={initialInspectId}
      onInspectConsumed={clearSearch}
      initialWizardInit={initialWizardInit}
      onWizardInitConsumed={clearSearch}
      initialColumnFilters={initialColumnFilters}
      onColumnFiltersConsumed={clearSearch}
    />
  );
}
